/**
 * SettingsGeneral — verbatim port of the canonical SettingsGeneral screen.
 * 2-column grid: Workspace | Event defaults / Privacy & compliance | Danger zone.
 */
import { useState, useEffect, useMemo, type ChangeEvent } from "react";
import { configureTenantLabels } from "../../lib/labels";
import { getGeneralSettings, updateGeneralSettings, listLocations } from "@v0y4ge/api-client";
import * as ApiClient from "@v0y4ge/api-client";
import { Banner, Btn, Chip, currencyName, currencySymbol, Field, Icon, Input, ISO_4217_ACTIVE, PageLayout, Section, supportedTimeZoneIds, T, TagLabel, ToggleRow } from "@v0y4ge/jrni-ui";
import { PageHeader } from "../../shell/chrome";
import { LOCATIONS_OPTS } from "../../shell/request-opts";
import { can, isAdmin, useTenant } from "../../shell/access";
import { AuditTrailDrawer } from "../../shell/AuditTrail";
import { logAction } from "../../lib/audit";
import { toast } from "../../shell/toast";
import SettingsShell from "./SettingsShell";
import { configureDateTime } from "../../lib/datetime";
import { useT, t as i18nT } from "../../lib/i18n";
import { FieldSelect, UrlField, ADDRESS_PLACEHOLDERS, ADDRESS_PRESETS, DATE_FORMAT_OPTIONS, formatAddressSample, formatDateSample, formatMoneySample, formatTimeSample, type FieldOption } from "./settings-fields";

/* ui-config — `24h`/`12h` ARE the contract's `timeFormat` enum domain (see the
 * GeneralSettings field), not rows of data. The sample beside each is computed
 * by formatTimeSample (Intl) at render, never written down. Built inside the
 * component so the labels go through the catalog. */
const TIME_FORMAT_VALUES = ["24h", "12h"] as const; // ui-config
const WEEK_START_OPTIONS: FieldOption[] = [
  { value: "monday", label: "Monday" },
  { value: "sunday", label: "Sunday" },
  { value: "saturday", label: "Saturday" },
];
const SYMBOL_POSITION_OPTIONS: FieldOption[] = [
  { value: "symbol-amount", label: "Symbol then amount", hint: "Fr. 1'234.56" },
  { value: "amount-symbol", label: "Amount then symbol", hint: "1'234.56 Fr." },
];
const DECIMAL_OPTIONS: FieldOption[] = [
  { value: ".", label: "Period", hint: "1234.56" },
  { value: ",", label: "Comma", hint: "1234,56" },
];
const THOUSANDS_OPTIONS: FieldOption[] = [
  { value: "'", label: "Apostrophe", hint: "1'234'567" },
  { value: ",", label: "Comma", hint: "1,234,567" },
  { value: ".", label: "Period", hint: "1.234.567" },
  { value: " ", label: "Space", hint: "1 234 567" },
  { value: "none", label: "None", hint: "1234567" },
];
const ADDRESS_COUNTRY_OPTIONS: FieldOption[] = Object.keys(ADDRESS_PRESETS).map((c) => ({ value: c }));

/* Time-zone choices come from the DATA LAYER — the tenant's own locations
 * (`listLocations`) plus whatever `defaultTimeZone` the settings row already
 * stores. No city list is baked here: a tenant that operates in Zürich and
 * Geneva gets Zürich and Geneva, not five American cities nobody asked for.
 * The offset in the hint is computed by Intl at render, so it stays correct
 * across DST instead of freezing "GMT−5" into the source. A tenant with no
 * locations served simply gets its stored zone and nothing invented.
 * The VALUE of the stored zone is passed through untouched, so the payload
 * shape (offset-suffixed rows included) never changes. */
const tzCity = (iana: string) => (iana.split("/").pop() || iana).replace(/_/g, " ");
function tzOffset(iana: string): string {
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone: iana, timeZoneName: "shortOffset" })
      .formatToParts(new Date()).find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch { return ""; }
}
const LANG_OPTIONS: FieldOption[] = [
  { value: "English (US)", hint: "en-US" },
  { value: "English (UK)", hint: "en-GB" },
  { value: "French", hint: "fr-FR" },
  { value: "German", hint: "de-DE" },
  { value: "Spanish", hint: "es-ES" },
  { value: "Japanese", hint: "ja-JP" },
];
const EVENT_TYPE_OPTIONS = ["Conference", "Workshop", "Webinar", "Summit", "Networking", "Other"];
/* Registration window — was a free-text string, which is not a value anyone should
 * have to phrase ("30 days before — 2 hours before"). It is TWO decisions: when
 * registration opens and when it closes. Both are pickers; the stored contract
 * value stays the same composed string, so the payload is unchanged. */
const REG_OPEN_OPTIONS = ["90 days before", "60 days before", "30 days before", "14 days before", "7 days before", "As soon as the event is published"];
const REG_CLOSE_OPTIONS = ["24 hours before", "12 hours before", "6 hours before", "2 hours before", "1 hour before", "At the event start time"];
const REG_DASH = " — ";
const composeWindow = (open: string, close: string) => `${open}${REG_DASH}${close}`;
/** Split a stored window string back into its two halves; null when it isn't one. */
function parseWindow(raw: string): { open: string; close: string } | null {
  const m = String(raw || "").split(/\s*[—–-]\s*(?=[^—–-]*$)/);
  if (m.length !== 2) return null;
  const open = m[0].trim(), close = m[1].trim();
  return open && close ? { open, close } : null;
}
/* Currency choices are the ISO 4217 active list, from the design system
 * (`ISO_4217_ACTIVE`) — names and symbols resolve through Intl at render, so
 * nothing is written down here. The six-row literal this replaces had no CHF,
 * which is what the operator hit (2026-08-06). Built inside the component so a
 * locale switch re-labels it. */
const RESIDENCY_OPTIONS: FieldOption[] = [
  { value: "US (Virginia)", label: "United States", hint: "us-east-1 · Virginia" },
  { value: "EU (Ireland)", label: "European Union", hint: "eu-west-1 · Ireland" },
  { value: "EU (Frankfurt)", label: "European Union", hint: "eu-central-1 · Frankfurt" },
  { value: "UK (London)", label: "United Kingdom", hint: "eu-west-2 · London" },
  { value: "APAC (Singapore)", label: "Asia Pacific", hint: "ap-southeast-1 · Singapore" },
];

/* Map the human-facing workspace selects to real BCP-47 locale / IANA tz so the
 * app's date & time formatting (lib/datetime) follows them. Adding a country
 * later = extend these maps, nothing else. */
const LANG_TO_LOCALE: Record<string, string> = {
  "English (US)": "en-US", "English (UK)": "en-GB", French: "fr-FR",
  German: "de-DE", Spanish: "es-ES", Japanese: "ja-JP",
};
const tzToIana = (v: string) => v.replace(/\s*\(GMT[^)]*\)\s*$/, "").trim();
function applyLocaleTz(lang: string, tz: string) {
  configureDateTime({ locale: LANG_TO_LOCALE[lang] || "en-US", timeZone: tzToIana(tz) || undefined });
}

/* Selects on this screen are FieldSelect from ./settings-fields (a real listbox:
 * our type/spacing, per-option hints, keyboard + ARIA), adopted 2026-08-04. The
 * native <select> is gone from this screen on purpose; do not reintroduce one.
 *
 * Two local composites used to live here and are GONE (2026-08-10, structural
 * DS-adoption pass):
 *  - `LabelInput` re-implemented jrni-ui's `Field` (label + control + hint/error)
 *    with the label as a <div>, so the control had no programmatic name and
 *    clicking the label focused nothing. Now `Field` + `Input`.
 *  - `ToggleRow` SHADOWED the jrni-ui export of the same name and rendered
 *    `<Toggle on={…}/>` with no `onChange` — and `Toggle` only takes a tab stop
 *    when `onChange` is a function, so all four switches were unreachable by
 *    keyboard. Now the DS `ToggleRow`, which passes the real handler through.
 * Do not reintroduce either: extend the jrni-ui component instead. */

/** Stable id so the workspace-name label can point at its own input. */
const WS_NAME_ID = "settings-general-workspace-name";
/** Same, for the format-template textarea and the factory-reset confirm field. */
const ADDR_TEMPLATE_ID = "settings-general-address-template";
const FR_CONFIRM_ID = "settings-general-factory-reset-confirm";

interface FactoryResetResult {
  tenantSlug: string; actor: string; dryRun: boolean;
  status: "dry_run" | "accepted"; storesWiped: string[]; seedsApplied: string[]; requestId: string;
}

export default function SettingsGeneral() {
  const t = useT();
  const canWrite = can("settings.write");
  const [auditOpen, setAuditOpen] = useState(false);

  // Factory reset — moved here from the retired Settings → Advanced tab
  // (docket ask, 2026-07-10): same behavior, same settings.factory_reset gate,
  // just relocated into the Danger zone where the other irreversible
  // workspace-wide actions already live. See the block below the 3 stock
  // Danger-zone buttons.
  const canFactoryReset = can("settings.factory_reset");
  const tenant = useTenant();
  const tenantSlug = tenant?.slug ?? "";
  const [frConfirm, setFrConfirm] = useState("");
  const [frBusy, setFrBusy] = useState<"dry" | "live" | null>(null);
  const [frResult, setFrResult] = useState<FactoryResetResult | null>(null);
  const [frErr, setFrErr] = useState<string | null>(null);
  const frMatches = frConfirm.length > 0 && frConfirm === tenantSlug;
  const runFactoryReset = async (dryRun: boolean) => {
    if (!frMatches) return;
    setFrBusy(dryRun ? "dry" : "live"); setFrErr(null);
    try {
      const op = (ApiClient as unknown as { factoryReset?: (slug: string, input: { confirmToken: string; dryRun?: boolean }) => Promise<FactoryResetResult> }).factoryReset;
      if (typeof op !== "function") {
        setFrErr("This action isn't available in this environment yet — the backend op is live but not yet exposed through the design-time contract. No data was touched.");
        return;
      }
      const res = await op(tenantSlug, { confirmToken: frConfirm, dryRun });
      setFrResult(res);
      if (!dryRun) { logAction("settings", "factory-reset", "Factory reset", "reset"); toast({ tone: "success", title: i18nT("settings.general.factoryResetRequestedRequestResRequestid.icu", { value0: res.requestId }) }); setFrConfirm(""); }
    } catch (e: unknown) {
      const code = (e as { code?: string })?.code;
      const msg = code === "factory_reset_disabled"
        ? "Factory reset isn't enabled for this tenant. It's opt-in per tenant and off by default."
        : code === "confirm_token_mismatch"
        ? "The typed value didn't match the tenant slug. Nothing was changed."
        : code === "forbidden"
        ? "Your role doesn't have permission to run this."
        : "Couldn't complete the request. Nothing was changed. Retry in a moment.";
      console.warn("factoryReset failed:", e);
      setFrErr(msg);
    } finally {
      setFrBusy(null);
    }
  };

  // #659: workspace name hydrates from the real general-settings row — no baked tenant literal.
  const [wsName,    setWsName]    = useState("");
  const [pubUrl,    setPubUrl]    = useState("");
  const [tz,        setTz]        = useState("");
  const [lang,      setLang]      = useState("English (US)");
  const [evType,    setEvType]    = useState("Conference");
  const [currency,  setCurrency]  = useState("USD");
  const [regWindow, setRegWindow] = useState("30 days before — 2 hours before");
  const [waitlist,  setWaitlist]  = useState(true);
  const [dietary,   setDietary]   = useState(true);
  const [residency, setResidency] = useState("US (Virginia)");
  /* Regional formatting (2026-08-04): "Default language" alone was the wrong model — a
   * workspace picks its region's FORMATS, not just a UI language. Each of these is a
   * GeneralSettings field (optional in the contract) and drives a live preview. */
  const [dateFormat, setDateFormat] = useState("DD.MM.YYYY");
  const [timeFormat, setTimeFormat] = useState("24h");
  const [weekStart, setWeekStart] = useState("monday");
  const [symbolPos, setSymbolPos] = useState("symbol-amount");
  const [decimalSep, setDecimalSep] = useState(".");
  const [thousandsSep, setThousandsSep] = useState("'");
  const [addrCountry, setAddrCountry] = useState("Switzerland");
  const [addrFormat, setAddrFormat] = useState(ADDRESS_PRESETS.Switzerland);
  const [gdpr,      setGdpr]      = useState(true);
  const [optIn,     setOptIn]     = useState(true);
  const [saving,    setSaving]    = useState(false);
  /* Time-zone options, resolved from the tenant's served locations. */
  const [tzZones,   setTzZones]   = useState<string[]>([]);
  useEffect(() => {
    if (typeof listLocations !== "function") return;
    listLocations({ limit: 200 } as never, LOCATIONS_OPTS as never).then((r: unknown) => {
      const rows = ((r as Record<string, unknown>)?.locations ?? (r as Record<string, unknown>)?.data ?? []) as Record<string, unknown>[];
      const zones = rows
        .map((l) => (l.timeZone ?? l.timezone ?? l.time_zone ?? "") as string)
        .filter((z): z is string => typeof z === "string" && z.includes("/"));
      setTzZones(Array.from(new Set(zones)));
    }).catch((e: unknown) => console.warn("listLocations failed:", e));
  }, []);
  /* Dirty tracking + validation (2026-08-04 best-practice pass): Save is only
   * enabled when something actually changed AND every field is valid, Revert
   * restores the loaded row instead of asking the user to reload, and a failed
   * Save attempt reveals field errors that were still waiting for a blur. */
  const [loaded, setLoaded] = useState<Record<string, unknown> | null>(null);
  const [urlValid, setUrlValid] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const nameInvalid = wsName.trim().length === 0;
  const windowInvalid = regWindow.trim().length === 0;
  /* The two halves of the registration window. A stored value that doesn't split
   * cleanly (an older free-text row) is kept and offered as its own option, so
   * opening this screen can never silently rewrite it. */
  const regParts = parseWindow(regWindow);
  const regOpen = regParts ? regParts.open : regWindow;
  const regClose = regParts ? regParts.close : REG_CLOSE_OPTIONS[3];
  const regOpenOptions = useMemo(
    () => (REG_OPEN_OPTIONS.includes(regOpen) ? REG_OPEN_OPTIONS : [regOpen, ...REG_OPEN_OPTIONS]),
    [regOpen],
  );
  const regCloseOptions = useMemo(
    () => (REG_CLOSE_OPTIONS.includes(regClose) ? REG_CLOSE_OPTIONS : [regClose, ...REG_CLOSE_OPTIONS]),
    [regClose],
  );
  const formValid = urlValid && !nameInvalid && !windowInvalid;
  /* Zones the picker offers: the stored one (value passed through verbatim, so
   * Save never rewrites the row's string), then every zone the tenant's served
   * locations actually sit in, then the FULL IANA set from the design system
   * (`supportedTimeZoneIds`, ~420 zones — the platform's tz database, not a
   * baked city list). The last part is why: with locations that carry no zone,
   * this field rendered an empty "Select…" and could not be set at all
   * (operator, 2026-08-06). Tenant zones stay first so the common answer is at
   * the top; the Dropdown's filter handles the long tail. */
  const TZ_OPTIONS = useMemo<FieldOption[]>(() => {
    const storedIana = tzToIana(tz);
    const near = Array.from(new Set([...(storedIana ? [storedIana] : []), ...tzZones]));
    const row = (iana: string): FieldOption => ({
      value: iana === storedIana ? tz : iana,
      label: tzCity(iana),
      hint: [iana, tzOffset(iana)].filter(Boolean).join(" · "),
    });
    const rest = supportedTimeZoneIds().filter((z) => !near.includes(z));
    return [...near.map(row), ...rest.map(row)];
  }, [tz, tzZones]);
  const CURRENCY_OPTIONS = useMemo<FieldOption[]>(
    () => ISO_4217_ACTIVE.map((code) => ({ value: code, hint: [currencyName(code), currencySymbol(code)].filter(Boolean).join(" · ") })),
    [],
  );
  const TIME_FORMAT_OPTIONS = useMemo<FieldOption[]>(() => TIME_FORMAT_VALUES.map((v) => ({
    value: v,
    label: v === "24h" ? t("settings.general.timeFormat24h", "24 hours") : t("settings.general.timeFormat12h", "12 hours"),
    hint: formatTimeSample(v),
  })), [t]);
  const current = useMemo(() => ({
    wsName, pubUrl, tz, lang, evType, currency, regWindow, waitlist, dietary, residency, gdpr, optIn,
    dateFormat, timeFormat, weekStart, symbolPos, decimalSep, thousandsSep, addrCountry, addrFormat,
  }), [wsName, pubUrl, tz, lang, evType, currency, regWindow, waitlist, dietary, residency, gdpr, optIn,
    dateFormat, timeFormat, weekStart, symbolPos, decimalSep, thousandsSep, addrCountry, addrFormat]);
  /* Unknown baseline (the settings row never loaded in this environment) must not
   * lock the form: Save stays available exactly as it did before this pass, and
   * only Revert — which needs a baseline to restore — is held back. */
  const dirty = loaded ? JSON.stringify(loaded) !== JSON.stringify(current) : true;
  const revert = () => {
    if (!loaded) return;
    const l = loaded as unknown as typeof current;
    setWsName(l.wsName); setPubUrl(l.pubUrl); setTz(l.tz); setLang(l.lang);
    setEvType(l.evType); setCurrency(l.currency); setRegWindow(l.regWindow);
    setWaitlist(l.waitlist); setDietary(l.dietary); setResidency(l.residency);
    setGdpr(l.gdpr); setOptIn(l.optIn);
    setDateFormat(l.dateFormat); setTimeFormat(l.timeFormat); setWeekStart(l.weekStart);
    setSymbolPos(l.symbolPos); setDecimalSep(l.decimalSep); setThousandsSep(l.thousandsSep);
    setAddrCountry(l.addrCountry); setAddrFormat(l.addrFormat);
    setSubmitted(false);
    applyLocaleTz(l.lang, l.tz);
    toast({ tone: "info", title: t("settings.general.changesReverted", "Changes reverted"), subtitle: t("settings.general.backToTheLastSavedValues", "Back to the last saved values.") });
  };

  // #659/#693: hydrate EVERY field from the real general-settings row — no baked tenant literal.
  useEffect(() => {
    if (typeof getGeneralSettings !== "function") return;
    getGeneralSettings({ baseUrl: (window as unknown as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__ ?? "" } as never).then((r) => {
      const s = (r as Record<string, any>).settings ?? (r as Record<string, any>).general ?? r;
      if (!s) return;
      // #1 (LABEL-OVERRIDES-EXPEDIENT) — the per-tenant vocabulary map rides the same
      // settings payload (declared key: `labels`); window.__STUDIO_LABELS__ covers boot.
      configureTenantLabels((s.labels ?? s.vocabulary) as Record<string, string> | undefined);
      if (typeof s.workspaceName === "string" && s.workspaceName) setWsName(s.workspaceName);
      if (typeof s.publicUrl === "string" && s.publicUrl) setPubUrl(s.publicUrl);
      if (typeof s.defaultTimeZone === "string" && s.defaultTimeZone) setTz(s.defaultTimeZone);
      if (typeof s.defaultLanguage === "string" && s.defaultLanguage) setLang(s.defaultLanguage);
      if (typeof s.defaultEventType === "string" && s.defaultEventType) setEvType(s.defaultEventType);
      if (typeof s.defaultTicketCurrency === "string" && s.defaultTicketCurrency) setCurrency(s.defaultTicketCurrency);
      if (typeof s.defaultRegistrationWindow === "string" && s.defaultRegistrationWindow) setRegWindow(s.defaultRegistrationWindow);
      if (typeof s.allowWaitlistsByDefault === "boolean") setWaitlist(s.allowWaitlistsByDefault);
      if (typeof s.captureDietaryAtRegister === "boolean") setDietary(s.captureDietaryAtRegister);
      if (typeof s.dataResidency === "string" && s.dataResidency) setResidency(s.dataResidency);
      if (typeof s.gdprSarPortalEnabled === "boolean") setGdpr(s.gdprSarPortalEnabled);
      if (typeof s.doubleOptInRequired === "boolean") setOptIn(s.doubleOptInRequired);
      if (typeof s.dateFormat === "string" && s.dateFormat) setDateFormat(s.dateFormat);
      if (s.timeFormat === "12h" || s.timeFormat === "24h") setTimeFormat(s.timeFormat);
      if (typeof s.weekStartsOn === "string" && s.weekStartsOn) setWeekStart(s.weekStartsOn);
      if (typeof s.currencySymbolPosition === "string" && s.currencySymbolPosition) setSymbolPos(s.currencySymbolPosition);
      if (typeof s.decimalSeparator === "string" && s.decimalSeparator) setDecimalSep(s.decimalSeparator);
      if (typeof s.thousandsSeparator === "string" && s.thousandsSeparator) setThousandsSep(s.thousandsSeparator);
      if (typeof s.addressCountry === "string" && s.addressCountry) setAddrCountry(s.addressCountry);
      if (typeof s.addressFormat === "string" && s.addressFormat) setAddrFormat(s.addressFormat);
      // Boot the app-wide date/time formatting from the workspace settings
      // (language → BCP-47 locale, default tz → IANA) — lib/datetime re-formats
      // every mounted screen live via useDateTimeVersion.
      applyLocaleTz(
        (typeof s.defaultLanguage === "string" && s.defaultLanguage) || "English (US)",
        (typeof s.defaultTimeZone === "string" && s.defaultTimeZone) || "",
      );
      // Baseline for dirty tracking + Revert.
      setLoaded({
        wsName: (typeof s.workspaceName === "string" && s.workspaceName) || "",
        pubUrl: (typeof s.publicUrl === "string" && s.publicUrl) || "",
        tz: (typeof s.defaultTimeZone === "string" && s.defaultTimeZone) || "",
        lang: (typeof s.defaultLanguage === "string" && s.defaultLanguage) || "English (US)",
        evType: (typeof s.defaultEventType === "string" && s.defaultEventType) || "Conference",
        currency: (typeof s.defaultTicketCurrency === "string" && s.defaultTicketCurrency) || "USD",
        regWindow: (typeof s.defaultRegistrationWindow === "string" && s.defaultRegistrationWindow) || "30 days before — 2 hours before",
        waitlist: typeof s.allowWaitlistsByDefault === "boolean" ? s.allowWaitlistsByDefault : true,
        dietary: typeof s.captureDietaryAtRegister === "boolean" ? s.captureDietaryAtRegister : true,
        residency: (typeof s.dataResidency === "string" && s.dataResidency) || "US (Virginia)",
        gdpr: typeof s.gdprSarPortalEnabled === "boolean" ? s.gdprSarPortalEnabled : true,
        optIn: typeof s.doubleOptInRequired === "boolean" ? s.doubleOptInRequired : true,
        dateFormat: (typeof s.dateFormat === "string" && s.dateFormat) || "DD.MM.YYYY",
        timeFormat: s.timeFormat === "12h" ? "12h" : "24h",
        weekStart: (typeof s.weekStartsOn === "string" && s.weekStartsOn) || "monday",
        symbolPos: (typeof s.currencySymbolPosition === "string" && s.currencySymbolPosition) || "symbol-amount",
        decimalSep: (typeof s.decimalSeparator === "string" && s.decimalSeparator) || ".",
        thousandsSep: (typeof s.thousandsSeparator === "string" && s.thousandsSeparator) || "'",
        addrCountry: (typeof s.addressCountry === "string" && s.addressCountry) || "Switzerland",
        addrFormat: (typeof s.addressFormat === "string" && s.addressFormat) || ADDRESS_PRESETS.Switzerland,
      });
    }).catch((e: unknown) => console.warn("getGeneralSettings failed:", e));
  }, []);

  // #659/#693 round 2 (UIR-02, 2026-07-25): `updateGeneralSettings` is a real,
  // fully-typed PATCH op (verified in api-contracts + the built api-client) —
  // the earlier "no op exists" read was stale. Wire Save to it for real; the
  // only remaining gap is the mock handler (REQ-073-DRAFT), so this honestly
  // fails today and self-heals the moment that lands.
  const handleSaveGeneral = async () => {
    setSubmitted(true);
    if (!formValid) {
      toast({ tone: "warn", title: t("settings.general.checkTheHighlightedFields", "Check the highlighted fields"), subtitle: t("settings.general.nothingWasSavedFixTheFieldsMarkedInRed", "Nothing was saved — fix the fields marked in red and try again.") });
      return;
    }
    applyLocaleTz(lang, tz);
    setSaving(true);
    try {
      await updateGeneralSettings({
        workspaceName: wsName,
        publicUrl: pubUrl,
        defaultTimeZone: tz,
        defaultLanguage: lang,
        defaultEventType: evType,
        defaultTicketCurrency: currency,
        defaultRegistrationWindow: regWindow,
        allowWaitlistsByDefault: waitlist,
        captureDietaryAtRegister: dietary,
        dataResidency: residency,
        gdprSarPortalEnabled: gdpr,
        doubleOptInRequired: optIn,
        dateFormat,
        timeFormat,
        weekStartsOn: weekStart,
        currencySymbolPosition: symbolPos,
        decimalSeparator: decimalSep,
        thousandsSeparator: thousandsSep,
        addressCountry: addrCountry,
        addressFormat: addrFormat,
      } as any, { baseUrl: (window as unknown as { __STUDIO_API_BASE__?: string }).__STUDIO_API_BASE__ ?? "" } as never);
      logAction("settings", "general", "General settings", "updated");
      setLoaded({ ...current });
      setSubmitted(false);
      toast({ tone: "success", title: t("settings.general.generalSettingsSaved", "General settings saved") });
    } catch (e) {
      console.warn("updateGeneralSettings failed:", e);
      toast({ tone: "danger", title: t("settings.general.couldnTSaveGeneralSettings", "Couldn't save general settings"), subtitle: t("settings.general.nothingWasSavedRetryInAMoment", "Nothing was saved — retry in a moment.") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsShell activeTab="general">
      <PageHeader
        title={t("settings.general.general", "General")}
        subtitle={t("settings.general.workspaceDefaultsThatApplyToEveryEventUnlessOverridden", "Workspace defaults that apply to every event unless overridden")}
        actions={<>
          {isAdmin() ? <Btn variant="secondary" icon={<Icon.list size={14} />} onClick={() => setAuditOpen(true)}>{t("settings.general.auditTrail", "Audit trail")}</Btn> : null}
          <Btn variant="secondary" disabled={!loaded || !dirty} onClick={revert}>{t("settings.general.revert", "Revert")}</Btn>
          <Btn disabled={!canWrite || saving || !dirty || (submitted && !formValid)} onClick={handleSaveGeneral}>{saving ? t("settings.general.saving", "Saving…") : t("settings.general.saveChanges", "Save changes")}</Btn>
        </>}
      />
      {auditOpen ? <AuditTrailDrawer target={{ type: "settings", id: "general", name: t("settings.general.generalSettings", "General settings") }} onClose={() => setAuditOpen(false)} /> : null}
      <PageLayout layout="two-col">
        {/* Workspace */}
        <Section title={t("settings.general.workspace", "Workspace")}>
          {/* The invalid state is the Input's own `error` — it renders the red
              border + ring AND the message with role="alert", so the control and
              its explanation are ONE signal instead of a red paragraph beside an
              untouched field. Field owns the label (real <label htmlFor>) and the
              required marker. */}
          <Field label={t("settings.general.workspaceName", "Workspace name")} required htmlFor={WS_NAME_ID}>
            <Input
              id={WS_NAME_ID}
              value={wsName}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setWsName(e.target.value)}
              readOnly={!canWrite}
              error={submitted && nameInvalid ? t("settings.general.workspaceNameRequired", "Give the workspace a name — guests see it on confirmations.") : undefined}
            />
          </Field>
          <UrlField
            label={t("settings.general.publicUrl", "Public URL")}
            value={pubUrl}
            onChange={setPubUrl}
            onValidityChange={setUrlValid}
            forceShowError={submitted}
            placeholder="https://events.acme.com"
            help={t("settings.general.publicUrlHelp", "Where your booking pages live. https:// is added if you leave it out.")}
            readOnly={!canWrite}
            required
          />
          <FieldSelect label={t("settings.general.defaultTimeZone", "Default time zone")} value={tz} options={TZ_OPTIONS} onChange={(v) => { setTz(v); applyLocaleTz(lang, v); }} readOnly={!canWrite} help={t("settings.general.defaultTimeZoneHelp", "Every date and time in the Studio is shown in this zone unless a location overrides it.")} />
        </Section>

        {/* Regional formatting — timezone/date/time/week (operator ask 2026-08-04) */}
        <Section
          title={t("settings.general.regionalFormats", "Date & time formats")}
          description={t("settings.general.regionalFormatsSub", "How dates, times and weeks are written across the Studio and in guest-facing messages.")}
        >
          <div style={{ padding: `${T.space[3]} ${T.space[4]}`, background: T.n6, borderRadius: T.radius.md, display: "flex", alignItems: "baseline", gap: T.space[2], flexWrap: "wrap" }}>
            <span style={{ fontSize: T.text.label.fontSize, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: T.soft }}>{t("settings.general.preview", "Preview")}</span>
            <span style={{ fontFamily: T.fontMono, fontSize: T.text.h3.fontSize, color: T.n1 }}>{formatDateSample(dateFormat)} · {formatTimeSample(timeFormat)}</span>
          </div>
          <FieldSelect label={t("settings.general.interfaceLanguage", "Interface language")} value={lang} options={LANG_OPTIONS} onChange={(v) => { setLang(v); applyLocaleTz(v, tz); }} readOnly={!canWrite} help={t("settings.general.interfaceLanguageHelp", "The language the Studio is shown in. Formats below are set separately, so a German workspace can keep an English interface.")} />
          <FieldSelect label={t("settings.general.dateFormat", "Date format")} value={dateFormat} options={DATE_FORMAT_OPTIONS} onChange={setDateFormat} readOnly={!canWrite} />
          <FieldSelect label={t("settings.general.timeFormat", "Time format")} value={timeFormat} options={TIME_FORMAT_OPTIONS} onChange={setTimeFormat} readOnly={!canWrite} />
          <FieldSelect label={t("settings.general.weekStartsOn", "Week starts on")} value={weekStart} options={WEEK_START_OPTIONS} onChange={setWeekStart} readOnly={!canWrite} help={t("settings.general.weekStartsOnHelp", "Used by the calendar, schedules and every week-based report.")} />
        </Section>

        {/* Currency & numbers */}
        <Section
          title={t("settings.general.currencyNumbers", "Currency & numbers")}
          description={t("settings.general.currencyNumbersSub", "How money is written on tickets, invoices and reports.")}
        >
          <div style={{ padding: `${T.space[4]}`, background: T.n6, borderRadius: T.radius.md, textAlign: "center" }}>
            <span style={{ fontFamily: T.fontMono, fontSize: T.text.display.fontSize, fontWeight: 700, color: T.n1 }}>{formatMoneySample(currency, symbolPos, decimalSep, thousandsSep)}</span>
          </div>
          <FieldSelect label={t("settings.general.defaultTicketCurrency", "Default ticket currency")} value={currency} options={CURRENCY_OPTIONS} onChange={setCurrency} readOnly={!canWrite} help={t("settings.general.defaultTicketCurrencyHelp", "Applies to new paid events. Existing events keep the currency they were created with.")} />
          <FieldSelect label={t("settings.general.currencyFormat", "Currency format")} value={symbolPos} options={SYMBOL_POSITION_OPTIONS} onChange={setSymbolPos} readOnly={!canWrite} help={t("settings.general.currencyFormatHelp", "Where the symbol sits relative to the amount.")} />
          <FieldSelect label={t("settings.general.decimalSeparator", "Decimal separator")} value={decimalSep} options={DECIMAL_OPTIONS} onChange={setDecimalSep} readOnly={!canWrite} />
          <FieldSelect label={t("settings.general.thousandsSeparator", "Thousands separator")} value={thousandsSep} options={THOUSANDS_OPTIONS} onChange={setThousandsSep} readOnly={!canWrite} />
        </Section>

        {/* Address format */}
        <Section
          title={t("settings.general.addressFormat", "Address format")}
          description={t("settings.general.addressFormatSub", "How addresses are laid out wherever they're printed — confirmations, invoices, badges.")}
        >
          <pre style={{ margin: 0, padding: `${T.space[3]} ${T.space[4]}`, background: T.n6, borderRadius: T.radius.md, fontFamily: T.fontMono, fontSize: T.text.bodySm.fontSize, lineHeight: 1.55, color: T.n1, whiteSpace: "pre-wrap" }}>{formatAddressSample(addrFormat)}</pre>
          <FieldSelect
            label={t("settings.general.addressCountryPreset", "Country preset")}
            value={addrCountry}
            options={ADDRESS_COUNTRY_OPTIONS}
            onChange={(v) => { setAddrCountry(v); const p = ADDRESS_PRESETS[v]; if (p) setAddrFormat(p); }}
            readOnly={!canWrite}
            help={t("settings.general.addressCountryPresetHelp", "Picking a country fills the template below with that country's usual order.")}
          />
          {/* The label + hint stack is jrni-ui's `Field` (real <label htmlFor>, so
              clicking the label focuses the editor). The FIELD ITSELF stays raw —
              see the ui-raw note below. */}
          <Field
            label={t("settings.general.formatTemplate", "Format template")}
            htmlFor={ADDR_TEMPLATE_ID}
            hint={t("settings.general.formatTemplateHelp", "Line breaks are kept. Click a placeholder to add it at the end.")}
          >
            {/* The FIELD stays raw. Verified in packages/jrni-ui/src/Textarea.tsx
                (2026-08-10): the inner <textarea> hardcodes `fontFamily: T.font` and
                `fontSize: T.text.body.fontSize` INLINE, and the `style` prop lands on
                the outer wrapper div — so the monospace treatment this template editor
                needs is unreachable from a call site. Only `className` forwards to the
                field and jrni-tokens ships no mono utility class, so adopting Textarea
                needs a DS change (a `mono` prop), not a workaround here.
                ui-raw: monospace, 12/12 padding, T.radius.md (8), rows 4. `outline: none`
                PLUS data-jrni-component="textarea" — the same pairing jrni-ui's own
                Textarea uses: the attribute opts into the 1px focus-ring layer, and
                outline:none stops reset.css's global 2px outline stacking on top of
                it (which read heavier than every other control on the screen). */}
            <textarea
              id={ADDR_TEMPLATE_ID}
              value={addrFormat}
              onChange={(e) => setAddrFormat(e.target.value)}
              readOnly={!canWrite}
              rows={4}
              spellCheck={false}
              data-jrni-component="textarea"
              style={{ width: "100%", boxSizing: "border-box", outline: "none", padding: `${T.space[3]} ${T.space[3]}`, border: `1px solid ${T.n4}`, borderRadius: T.radius.md, background: canWrite ? T.white : T.n6, fontFamily: T.fontMono, fontSize: T.text.bodySm.fontSize, lineHeight: 1.6, color: T.n1, resize: "vertical" }}
            />
          </Field>
          <div style={{ display: "flex", flexWrap: "wrap", gap: T.space[2] }}>
            {ADDRESS_PLACEHOLDERS.map((p) => (
              <Chip
                key={p.token}
                disabled={!canWrite}
                onClick={() => setAddrFormat((f) => (f.endsWith("\n") || f.length === 0 ? f + p.token : `${f} ${p.token}`))}
              >
                <code style={{ fontFamily: T.fontMono, fontSize: T.text.label.fontSize, fontWeight: 600 }}>{p.token}</code>
                <span style={{ color: T.soft }}>{p.label}</span>
              </Chip>
            ))}
          </div>
        </Section>

        {/* Event defaults */}
        <Section title={t("settings.general.eventDefaults", "Event defaults")}>
          <FieldSelect label={t("settings.general.defaultEventType", "Default event type")} value={evType} options={EVENT_TYPE_OPTIONS} onChange={setEvType} readOnly={!canWrite} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: T.space[3] }}>
            <FieldSelect
              label={t("settings.general.registrationOpens", "Registration opens")}
              value={regOpen}
              options={regOpenOptions}
              onChange={(v) => setRegWindow(composeWindow(v, regClose))}
              readOnly={!canWrite}
            />
            <FieldSelect
              label={t("settings.general.registrationCloses", "Registration closes")}
              value={regClose}
              options={regCloseOptions}
              onChange={(v) => setRegWindow(composeWindow(regOpen, v))}
              readOnly={!canWrite}
            />
          </div>
          <p style={{ fontSize: T.text.label.fontSize, color: T.soft, lineHeight: 1.5, margin: `${T.space[2]} 0 0` }}>
            {t("settings.general.registrationWindowHelp", "When registration opens and closes, relative to the event start.")}
            {" "}<strong style={{ color: T.n2, fontWeight: 600 }}>{regWindow}</strong>
          </p>
          {/* DS ToggleRow: the switch gets the REAL handler, so it is a focusable
              role="switch" with Space/Enter — the local copy passed none and left
              all four of these unreachable by keyboard. `disabled` is the DS's only
              non-editable state (no `readOnly`), and it dims honestly instead of
              leaving a locked switch looking live. */}
          <ToggleRow title={t("settings.general.allowWaitlistsByDefault", "Allow waitlists by default")} description={t('settings.general.sub')} checked={waitlist} onChange={setWaitlist} disabled={!canWrite} />
          <ToggleRow title={t("settings.general.captureDietaryAtRegister", "Capture dietary at register")} description={t('settings.general.sub2')} checked={dietary} onChange={setDietary} disabled={!canWrite} />
        </Section>

        {/* Privacy & compliance */}
        <Section title={t("settings.general.privacyCompliance", "Privacy & compliance")}>
          <FieldSelect label={t("settings.general.dataResidency", "Data residency")} value={residency} options={RESIDENCY_OPTIONS} onChange={setResidency} readOnly={!canWrite} help={t("settings.general.dataResidencyHelp", "Where guest data is stored. Changing this moves future records only — contact support to migrate history.")} />
          {/* §3i (2026-07-19): retention shown as clearly-labeled product defaults,
              never tenant values in inputs — no retention-settings op exists yet. */}
          <div>
            <div style={{ fontSize: T.text.bodySm.fontSize, fontWeight: 600, color: T.n1, marginBottom: T.space[1] }}>{t("settings.general.dataRetention", "Data retention")}</div>
            <p style={{ ...T.text.help, color: T.soft, margin: 0 }}>{t("settings.general.productDefaultsNotYetConfigurablePerWorkspaceGuestRecordsAreKept36", "Product defaults — not yet configurable per workspace: guest records are kept 36 months after their last event; audit logs 90 days, then redacted. Contact support to adjust for this workspace.")}</p>
          </div>
          <ToggleRow title={t("settings.general.gdprSubjectAccessRequestPortalEnabled", "GDPR · Subject Access Request portal enabled")} checked={gdpr} onChange={setGdpr} disabled={!canWrite} />
          <ToggleRow title={t("settings.general.requireDoubleOptInForMarketingEmails", "Require double opt-in for marketing emails")} checked={optIn} onChange={setOptIn} disabled={!canWrite} />
        </Section>

        {/* Danger zone */}
        <Section
          title={t("settings.general.dangerZone", "Danger zone")}
          description={t("settings.general.irreversibleWorkspaceWideActions", "Irreversible workspace-wide actions.")}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: T.space[2] }}>
            <Btn variant="secondary" icon={<Icon.download size={14} />} onClick={() => toast({ tone: "info", title: t("settings.general.exportNotAvailableInThisPreview", "Export not available in this preview"), subtitle: t("settings.general.contactSupportForAWorkspaceDataExport", "Contact support for a workspace data export.") })}>{t("settings.general.exportEverything", "Export everything")}</Btn>
            <Btn variant="secondary" icon={<Icon.refresh size={14} />} onClick={() => toast({ tone: "warn", title: t("settings.general.transferOwnership", "Transfer ownership"), subtitle: t("settings.general.contactSupportToTransferWorkspaceOwnership", "Contact support to transfer workspace ownership.") })}>{t("settings.general.transferOwnership", "Transfer ownership")}</Btn>
            <Btn variant="primary" danger icon={<Icon.trash size={14} />} onClick={() => toast({ tone: "danger", title: t("settings.general.workspaceDeletion", "Workspace deletion"), subtitle: t("settings.general.contactSupportToDeleteThisWorkspace", "Contact support to delete this workspace.") })}>{t("settings.general.deleteWorkspace", "Delete workspace")}</Btn>
          </div>

          {canFactoryReset ? (
            <div style={{ marginTop: T.space[2], border: `1px solid ${T.red1}`, borderRadius: T.radius.md, overflow: "hidden" }}>
              <div style={{ padding: `${T.space[3]} ${T.space[4]}`, background: T.red2, display: "flex", alignItems: "center", gap: T.space[2] }}>
                <Icon.warn size={16} style={{ color: T.red1, flexShrink: 0 }} />
                {/* The pill is the DS `TagLabel` (alias `Pill`), not a private
                    brand tint. NOTE the fill moves T.p4 -> T.p3 (TagLabel's
                    PILL_PALETTE.primary, the same pair toneColors('primary')
                    returns): a visible step up in tint strength, deliberate —
                    T.p4 was a fourth privately-chosen brand wash and read weaker
                    than every other primary pill in the app. */}
                <div style={{ fontSize: T.text.body.fontSize, fontWeight: 700, color: T.n1 }}>{t("settings.general.factoryResetToSeeds", "Factory reset to seeds")}<TagLabel tone="primary" style={{ marginLeft: T.space[1] }}>{t("settings.general.admin", "Admin")}</TagLabel></div>
              </div>
              <div style={{ padding: T.space[4], display: "flex", flexDirection: "column", gap: T.space[3] }}>
                <p style={{ margin: 0, fontSize: T.text.bodySm.fontSize, color: T.n1, lineHeight: 1.6 }}>
                  {t("settings.general.this", "This")}{" "}<strong>{t("settings.general.permanentlyDeletesThisTenantSData", "permanently deletes this tenant's data")}</strong> {t("settings.general.eventsGuestsLocationsStaffEverythingMutableAndReAppliesItsDeclarativePla", "— events, guests, locations, staff, everything mutable — and re-applies its declarative platform seeds.")}{" "}<strong>{t("settings.general.thisCannotBeUndone", "This cannot be undone.")}</strong> {t("settings.general.onlyRunThisOnADisposableTestTenant", "Only run this on a disposable test tenant.")}</p>
                <div>
                  {/* htmlFor added 2026-08-10 — this was a real <label> that wrapped
                      no control and pointed at none, so clicking it focused nothing. */}
                  <label htmlFor={FR_CONFIRM_ID} style={{ fontSize: T.text.label.fontSize, fontWeight: 600, color: T.n2, display: "block", marginBottom: T.space[2] }}>
                    {t("settings.general.typeTheTenantSlugToConfirm", "Type the tenant slug to confirm")}{" "}{tenantSlug ? <> (<code style={{ background: T.n6, padding: `${T.space[1]} ${T.space[2]}`, borderRadius: T.radius.sm }}>{tenantSlug}</code>)</> : null}
                  </label>
                  <Input id={FR_CONFIRM_ID} value={frConfirm} onChange={(e: ChangeEvent<HTMLInputElement>) => setFrConfirm(e.target.value)} placeholder={tenantSlug || t("settings.settingsGeneral.tenantSlug", "tenant-slug")}
                    style={{ maxWidth: 340 }} trailing={frMatches ? <span style={{ color: T.green1, display: "inline-flex" }}><Icon.check size={14} /></span> : undefined} />
                </div>
                {/* Both callouts are the DS `Banner` at size="compact" ("inline-in-a-card").
                    They were the same hand-rolled recipe eleven lines apart with the tones
                    swapped — now one component, one toneColors() palette, plus a tone icon
                    (so tone is not colour-only) and Banner's own live region: danger renders
                    role="alert", success role="status", so a failed reset is announced. */}
                {frErr ? <Banner tone="danger" size="compact">{frErr}</Banner> : null}
                {frResult ? (
                  <Banner tone="success" size="compact">
                    {frResult.status === "dry_run" ? t('settings.general.text') : /* TODO(i18n): replace template-literal with t('settings.general.text2', { ... }) */`Reset accepted (request ${frResult.requestId}) — this runs asynchronously and may take a few minutes.`}
                    {frResult.storesWiped.length ? <div style={{ marginTop: T.space[2] }}>{t("settings.general.stores", "Stores:")}{" "}{frResult.storesWiped.join(", ")}</div> : null}
                    {frResult.seedsApplied.length ? <div style={{ marginTop: T.space[1] }}>{t("settings.general.seeds", "Seeds:")}{" "}{frResult.seedsApplied.join(", ")}</div> : null}
                  </Banner>
                ) : null}
                <div style={{ display: "flex", gap: T.space[2] }}>
                  <Btn variant="secondary" size="sm" disabled={!frMatches || !!frBusy} onClick={() => runFactoryReset(true)}>{frBusy === "dry" ? t("settings.settingsGeneral.runningPreview", "Running preview…") : t("settings.settingsGeneral.previewDryRun", "Preview (dry run)")}</Btn>
                  <Btn variant="primary" size="sm" danger disabled={!frMatches || !!frBusy} onClick={() => runFactoryReset(false)}>{frBusy === "live" ? t("settings.settingsGeneral.resetting", "Resetting…") : t("settings.settingsGeneral.factoryResetNow", "Factory reset now")}</Btn>
                </div>
              </div>
            </div>
          ) : null}
        </Section>
      </PageLayout>
    </SettingsShell>
  );
}
